import React from 'react';

/**
 * Modal - A reusable modal component with consistent styling
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {function} props.onClose - Function to call when the modal is closed
 * @param {string} props.title - The modal title
 * @param {React.ReactNode} props.children - The modal content
 * @param {React.ReactNode} props.footer - Optional footer content (e.g., action buttons)
 * @param {string} props.size - The modal size ('sm', 'md', 'lg', 'xl')
 */
const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
}) => {
  if (!isOpen) return null;

  // Size classes
  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-full mx-4'
  };

  // Handle click outside the modal content
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby={title}
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
        onClick={handleBackdropClick}
      ></div>

      {/* Modal container */}
      <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
        <div
          className={`app-modal-surface relative transform overflow-hidden text-left transition-all sm:my-8 ${sizeClasses[size]} w-full`}
        >
          {/* Gradient accent bar */}
          <div className="h-1" style={{ background: 'linear-gradient(90deg, #3b82f6, #6366f1, #8b5cf6)' }} />

          {/* Header */}
          <div className="app-modal-header flex items-center justify-between px-5 py-3.5">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-slate-100 flex items-center gap-2">
              <span className="w-1 h-4 rounded-full bg-gradient-to-b from-blue-500 to-indigo-500 inline-block flex-shrink-0" />
              {title}
            </h3>
            <button
              type="button"
              className="rounded-full p-1.5 text-gray-400 dark:text-slate-400 hover:bg-indigo-100/60 dark:hover:bg-slate-700 hover:text-indigo-600 dark:hover:text-slate-200 focus:outline-none transition-colors"
              onClick={onClose}
            >
              <span className="sr-only">Close</span>
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="app-modal-body p-5 text-sm form-compact dark:text-slate-200">
            {children}
          </div>

          {/* Footer */}
          {footer && (
            <div className="app-modal-header px-5 py-3.5 flex justify-end space-x-3 border-t border-indigo-100/60 dark:border-slate-700">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Modal;
